/**
 * WARNING: Usage of this is DEPRECATED. DO NOT USE
 */

/* eslint-disable new-cap, */
import React from 'react';
import TestUtils from 'react-dom/test-utils';
import ShallowRenderer from 'react-test-renderer/shallow';

import _ from 'underscore';
import createMockComponentContext from 'vendor/cnpm/fluxible.v0-4/utils/createMockComponentContext';

/**
 * Mock the context around a React Component for testing purposes.
 */
const _ContextualRenderer = function () {
  this.Component = null;
  this.InitialComponent = null;
  this.instance = null;
  this.props = null;
  this._childContextTypes = {};
  this._getChildContext = null;
  this._getChildContextObj = {};
  return this;
};

/**
 * Start the render configuration chain.
 * @param {ReactComponent} InitialComponent
 * @param {props} object of properties to pass to the component during render
 * @returns {ContextualRenderer}
 */
_ContextualRenderer.prototype.render = function (InitialComponent, props) {
  this.Component = InitialComponent;
  this.InitialComponent = InitialComponent;
  this.props = props;
  return this;
};

/**
 * End the render chain by actually rendering into Document.
 * @returns {ReactNode}
 */
_ContextualRenderer.prototype.intoDocument = function () {
  const that = this;

  this.Component = React.createFactory(
    React.createClass({
      childContextTypes: that._childContextTypes,
      getChildContext: that._getChildContext,
      render() {
        that.instance = that.InitialComponent(that.props);
        return that.instance;
      },
    })
  );

  const ReactNode = TestUtils.renderIntoDocument(this.Component(this.props));

  return TestUtils.findRenderedComponentWithType(ReactNode, this.instance.type);
};

/*
 * Sends the currently contextualized component into the shallow renderer.
 * Due to limitations in the shallow rendering system, `this.instance` cannot be evaluated
 * using methods like `TestUtils.findRenderedComponentWithType`, but `this.instance` is being
 * exported here since the shallowRenderer doesn't natively expose the ability to call
 * ReactInstance#setState.
 * In order to make assertions about how rendered output, one much call `getRenderOutput`
 * For more information, see https://facebook.github.io/react/docs/test-utils.html#shallow-rendering
 *
 * @returns {object} returnObj
 * @returns {ReactInstance} returnObj.instance
 * @returns {ContextualRenderer} returnObj.renderer
 */
_ContextualRenderer.prototype.intoShallowRenderer = function () {
  this.renderer = new ShallowRenderer();
  this.Component = React.createFactory(this.InitialComponent);

  this.renderer.render(this.Component(this.props), this._getChildContextObj);

  // May not be evaluated using (find|scry)RenderedComponent methods (yet).
  this.instance = this.renderer._instance;

  return {
    instance: this.instance,
    renderer: this.renderer,
  };
};

/**
 * Add React context to what's being renderered.
 * @param  {object} contextObj
 * @return {ContextualRenderer}
 */
_ContextualRenderer.prototype.withContext = function (contextObj) {
  const isV1 =
    _(contextObj)
      .chain()
      .values()
      .filter(function (v) {
        return v && v.type;
      })
      .value().length === _(contextObj).keys().length;

  if (isV1) {
    // TODO: Somehow throw a deprecation warning here.
    // Remove the below once we migrate off to v1.
    _(contextObj).each(
      function (v, k) {
        this._childContextTypes[k] = v.type;
        this._getChildContextObj[k] = v.value;
      }.bind(this)
    );

    this._getChildContext = function () {
      return this._getChildContextObj;
    }.bind(this);
  } else {
    _(contextObj).each(
      function (v, k) {
        this._childContextTypes[k] = React.PropTypes.any;
        this._getChildContextObj[k] = v;
      }.bind(this)
    );

    this._getChildContext = function () {
      return this._getChildContextObj;
    }.bind(this);
  }

  return this;
};

// TODO rename this, has nothing to do with Epic
/**
 * Add React context to what's being renderered.
 * @param  {object} contextObj
 * @return {ContextualRenderer}
 */
_ContextualRenderer.prototype.withEpicContext = function () {
  const track = function () {};
  track.handle = function () {};

  return this.withContext({ track });
};

/**
 * Add fluxible required methods executeAction and getStore
 * to context. Pass in the stubs or the functions will do no-op.
 * @param {array} stores - array of stores to bootstrap your fluxible context.
 */
_ContextualRenderer.prototype.withFluxibleContext = function (options) {
  return this.withContext({
    executeAction: options.executeAction || function () {},
    getStore: options.getStore || function () {},
  });
};

/**
 * Add fluxible functional component context with registered stores and
 * bootstrap the stores with data.
 * @param {object} options
 * @param {Array.<FluxibleStore>} options.stores
 * @param {object} Mapping of Store's by name to the data that should be in the store.
 */
_ContextualRenderer.prototype.withFluxibleComponentContext = function (options) {
  const mockFluxibleComponentContext = createMockComponentContext({ stores: options.stores });
  Object.keys(options.initialData).forEach(function (storeName) {
    const store = mockFluxibleComponentContext.getStore(storeName);
    if (store.constructor.name === 'WrappedReducerStore') {
      // Reducers put all state in the `state` property
      store.state = options.initialData[storeName];
    } else {
      // Standard stores stick state straight on the store's `this`
      _.extend(store, options.initialData[storeName]);
    }

    // Force an emitChange();
    store.emitChange();
  });
  this.fluxibleContext = mockFluxibleComponentContext;

  return this.withContext({
    executeAction: mockFluxibleComponentContext.executeAction,
    getStore: mockFluxibleComponentContext.getStore,
  });
};

/**
 * Handle ReactRouter stubs for version 0.13 and above.
 * @param {object} stubs - overwrite any stubs with actual functions, otherwise they are no-op
 */
_ContextualRenderer.prototype.withRouterContext = function (stubs) {
  const routerStub = require('bundles/test/stubs/router').default;

  return this.withContext({
    router: routerStub(stubs),
  });
};

const ContextualRenderer = new _ContextualRenderer();

export default ContextualRenderer;
